import React from 'react';
import { X, Search } from 'lucide-react';
import { playPulseSearchBeacon } from '../utils/pulseAudio';

interface ArcSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  id?: string;
  /** Default Pulse navy. Light is white/dark-text for Planner search only. */
  variant?: 'pulse' | 'light';
}

/**
 * Standardized Pulse-Scoped Search Surface
 * 
 * Features:
 * - Flat two-tone Pulse mark (white + restrained Pulse blue)
 * - Compact graphite/navy input with subtle border
 * - Restrained specular hairline
 * - Instant clear button when populated
 */
export const ArcSearchInput: React.FC<ArcSearchInputProps> = ({
  value,
  onChange,
  placeholder = 'Search records...',
  className = '',
  inputClassName = '',
  onKeyDown,
  autoFocus = false,
  id,
  variant = 'pulse'
}) => {
  const isLight = variant === 'light';
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.trim().length > 0 && typeof window !== 'undefined' && window.innerWidth >= 640) {
      playPulseSearchBeacon();
    }
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <div
      className={`relative flex items-center gap-2 px-2.5 py-1.5 rounded-xl transition-all ${
        isLight
          ? 'border border-slate-200/90 bg-white hover:border-slate-300 shadow-xs focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300'
          : 'mobile-ops-input sm:border sm:border-slate-700/80 sm:hover:border-slate-600/90 sm:bg-gradient-to-b sm:from-[#182436] sm:to-[#101826] sm:shadow-[0_2px_8px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.06)] focus-within:border-sky-400/70 focus-within:ring-1 focus-within:ring-sky-500/25'
      } ${className}`}
    >
      {/* Restrained specular top hairline */}
      {!isLight && (
        <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-slate-300/10 to-transparent pointer-events-none" />
      )}

      {/* Phone-width: Standard Search icon (clean, unbranded local filter). Desktop/Tablet (sm+): Hard-frozen mark */}
      <div className={`flex sm:hidden items-center pl-0.5 shrink-0 select-none ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
        <Search className="w-3.5 h-3.5 shrink-0" />
      </div>
      <div className={`hidden sm:flex items-center pl-0.5 shrink-0 select-none ${isLight ? 'text-slate-500' : ''}`}>
        {isLight ? (
          <Search className="w-3.5 h-3.5 shrink-0" />
        ) : (
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" className="stroke-sky-400" />
            <path d="M21 21l-4.35-4.35" className="stroke-white" />
          </svg>
        )}
      </div>

      {/* Search Input */}
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={`w-full bg-transparent text-xs focus:outline-none tracking-normal font-sans ${
          isLight ? 'text-slate-800 placeholder:text-slate-400' : 'text-white placeholder:text-slate-500'
        } ${inputClassName}`}
      />

      {/* Clear Button */}
      {value.trim().length > 0 && (
        <button
          type="button"
          onClick={() => onChange('')}
          className={`p-0.5 rounded transition-colors shrink-0 cursor-pointer ${
            isLight ? 'text-slate-400 hover:text-slate-800' : 'text-slate-400 hover:text-white'
          }`}
          title="Clear search"
          aria-label="Clear search"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};
